import { useRef, useState } from "react";
import { FileUp, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { api, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

export default function CsvImport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/csv/products/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) { toast.error(getErrorMessage(err, "Preview failed")); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview || !file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/csv/products/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Applied ${data.count} row${data.count === 1 ? "" : "s"}`);
      setPreview({ ...preview, applied: data });
    } catch (err) { toast.error(getErrorMessage(err, "Apply failed")); }
    finally { setBusy(false); }
  };

  const download = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/csv/products`;
    const token = localStorage.getItem("db_token");
    // Do a fetch to get the file with Authorization header.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = "products.csv"; a.click();
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <>
      <PageHeader title="CSV import & export" subtitle="Round-trip your catalog. Import validates every row before touching data." testid="page-csv"
        actions={<Button variant="outline" className="h-9" onClick={download} data-testid="csv-export"><FileUp size={14} className="rotate-180" /> Export catalog</Button>}
      />
      <div className="p-6 space-y-4 max-w-5xl">
        <div className="bg-white border border-[#E2E4E0] rounded-lg p-5">
          <div className="flex items-center gap-3">
            <button onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-[#E2E4E0] hover:bg-[#F9F9F8]" data-testid="csv-choose">
              <FileUp size={14} /> Choose CSV
            </button>
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="csv-input" />
            <div className="text-sm text-[#5C665A]" data-testid="csv-filename">{file ? file.name : "No file selected"}</div>
            <Button data-testid="csv-preview-btn" disabled={!file || busy} onClick={doPreview} className="ml-auto h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white">Preview</Button>
          </div>
        </div>

        {preview && (
          <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E4E0] bg-[#F9F9F8]">
              <div className="db-h font-semibold text-sm">Preview</div>
              <div className="flex items-center gap-2 text-xs">
                <StatBadge label={`total ${preview.stats.total}`} tone="neutral" testid="csv-stat-total" />
                <StatBadge label={`update ${preview.stats.updates}`} tone="ok" testid="csv-stat-updates" />
                <StatBadge label={`add ${preview.stats.add_variants}`} tone="ok" testid="csv-stat-add" />
                <StatBadge label={`create ${preview.stats.creates}`} tone="warn" testid="csv-stat-create" />
                <StatBadge label={`errors ${preview.stats.errors}`} tone="bad" testid="csv-stat-errors" />
              </div>
              <Button data-testid="csv-apply-btn" disabled={busy || preview.applied} onClick={apply} className="ml-auto h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white disabled:opacity-50">
                <ArrowRight size={14} /> {preview.applied ? "Applied" : "Apply changes"}
              </Button>
            </div>
            <div className="max-h-[540px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F9F8] text-[#5C665A] sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 db-h font-semibold w-24">Action</th>
                    <th className="text-left px-3 py-2 db-h font-semibold">Product · Size</th>
                    <th className="text-left px-3 py-2 db-h font-semibold w-40">SKU</th>
                    <th className="text-right px-3 py-2 db-h font-semibold w-24">Base €</th>
                    <th className="text-right px-3 py-2 db-h font-semibold w-24">Stock</th>
                    <th className="text-left px-3 py-2 db-h font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-t border-[#E2E4E0]" data-testid={`csv-row-${i}`}>
                      <td className="px-3 py-1.5">
                        {r.errors.length ? <Badge className="bg-[#FDF3F3] text-[#C94A4A] border-transparent">error</Badge>
                          : <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent">{r.action.replace("_", " ")}</Badge>}
                      </td>
                      <td className="px-3 py-1.5">{r.row.product_name} · <span className="text-[#5C665A]">{r.row.size}</span></td>
                      <td className="px-3 py-1.5 db-mono text-xs">{r.row.sku}</td>
                      <td className="px-3 py-1.5 text-right db-mono">{r.row.base_price}</td>
                      <td className="px-3 py-1.5 text-right db-mono">{r.row.stock_qty}</td>
                      <td className="px-3 py-1.5">
                        {r.errors.map((e, k) => <div key={k} className="text-xs text-[#C94A4A] inline-flex items-center gap-1"><AlertTriangle size={12} />{e}</div>)}
                        {r.warnings.map((w, k) => <div key={k} className="text-xs text-[#D4A373]">{w}</div>)}
                        {preview.applied && (preview.applied.applied.find((a) => a.sku === r.row.sku)?.ok ? <span className="text-xs text-[#40916C] inline-flex items-center gap-1"><CheckCircle2 size={12} /> applied</span> : null)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function StatBadge({ label, tone, testid }) {
  const map = { neutral: "bg-neutral-100 text-neutral-700", ok: "bg-[#E9F5EC] text-[#1B4332]", warn: "bg-amber-50 text-amber-800", bad: "bg-[#FDF3F3] text-[#C94A4A]" };
  return <span data-testid={testid} className={`px-2 py-0.5 rounded ${map[tone] || map.neutral} db-mono`}>{label}</span>;
}
